import React, { useEffect, useState } from "react";
import { apiUrl } from "../ApiManager/AxiosHeaders";
import * as FaIcons from "react-icons/fa";
import Swal from "sweetalert2";
import "../HRManagement/leavePolicies.css";
import "./leavePortal.css";
import LeaveRequestsPanel from "./LeaveRequestsPanel";

function UserLeave() {
  const [showSkeleton, setShowSkeleton] = useState(false);
  const [userId, setUserId] = useState("");
  const [leaveData, setLeaveData] = useState([]);
  const [leaveModal, setLeaveModal] = useState(false);
  const [loadingButton, setLoadingButton] = useState(false);
  const [selectedLeave, setSelectedLeave] = useState(null);
  const [leaveType, setLeaveType] = useState("Full Day");
  const [formData, setFormData] = useState({
    startDate: "",
    endDate: "",
    description: "",
    document: null,
    startTime: "",
    endTime: "",
  });
  const [errors, setErrors] = useState({});
  const [requestRefreshKey, setRequestRefreshKey] = useState(0);

  useEffect(() => {
    const user_id = JSON.parse(localStorage.getItem("user_id"));
    if (user_id) {
      setUserId(user_id);
    }
  }, []);

  useEffect(() => {
    if (userId) {
      fetchUserLeaveEntitlement();
    }
  }, [userId]);

  const formatPeriodDate = (date) => {
    if (!date) return "—";
    return new Date(date).toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  const openApplyModal = (preselectedLeave = null) => {
    setErrors({});
    setLeaveModal(true);
    setSelectedLeave(preselectedLeave);
    setLeaveType("Full Day");
    setFormData({
      startDate: "",
      endDate: "",
      description: "",
      document: null,
      startTime: "",
      endTime: "",
    });
  };

  const fetchUserLeaveEntitlement = async () => {
    setShowSkeleton(true);
    try {
      const response = await apiUrl.get(
        `/leave-entitlements/user/${userId}/active`,
      );
      if (response?.status) {
        const allActive = response?.data?.data || [];
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        setLeaveData(
          allActive.filter((e) => {
            if (e.is_current_period !== undefined) return e.is_current_period;
            if (!e.period_start || !e.period_end) return true;
            const start = new Date(e.period_start);
            const end = new Date(e.period_end);
            start.setHours(0, 0, 0, 0);
            end.setHours(23, 59, 59, 999);
            return start <= today && end >= today;
          }),
        );
      }
    } catch (error) {
      // Fallback for older API shape
      try {
        const fallback = await apiUrl.get(`/user/leave-entitlements/${userId}`);
        if (fallback?.status) {
          const all = fallback?.data?.data || [];
          setLeaveData(
            all.filter(
              (e) =>
                e.is_active !== false &&
                e.is_active !== 0 &&
                e.status !== "archived" &&
                e.status !== "inactive",
            ),
          );
        }
      } catch (inner) {
        console.log(inner);
      }
    } finally {
      setShowSkeleton(false);
    }
  };

  // handle leave
  const handleLeaveChange = (e) => {
    const leave = leaveData?.find(
      (item) => item.id === parseInt(e.target.value),
    );
    setSelectedLeave(leave || null);
    setLeaveType("Full Day");
  };
  // user apply for leave
  const handleSubmitLeave = async () => {
    const newErrors = {};

    if (!selectedLeave) {
      newErrors.leave_type = "Please select a leave type";
    }

    if (!formData.startDate) {
      newErrors.startDate = "Start date is required";
    }

    // For Full Day, end date should be provided or default to start date
    const endDate =
      leaveType === "Full Day"
        ? formData.endDate || formData.startDate
        : formData.startDate;

    if (
      leaveType === "Full Day" &&
      formData.endDate &&
      new Date(formData.endDate) < new Date(formData.startDate)
    ) {
      newErrors.endDate = "End date must be on or after start date";
    }

    if (!formData.description?.trim()) {
      newErrors.description = "Reason for leave is required";
    }

    if (selectedLeave?.leave_policy?.requires_document && !formData.document) {
      newErrors.document = "Supporting document is required";
    }

    if (leaveType !== "Full Day") {
      if (!formData.startTime) {
        newErrors.startTime = "Start time is required";
      }
      if (!formData.endTime) {
        newErrors.endTime = "End time is required";
      }

      if (formData.startTime && formData.endTime) {
        const [startHour, startMin] = formData.startTime.split(":").map(Number);
        const [endHour, endMin] = formData.endTime.split(":").map(Number);
        const startMinutes = startHour * 60 + startMin;
        const endMinutes = endHour * 60 + endMin;

        if (endMinutes <= startMinutes) {
          newErrors.endTime = "End time must be after start time";
        } else {
          const hours = (endMinutes - startMinutes) / 60;
          const tolerance = 0.01;

          if (leaveType === "Half Day" && Math.abs(hours - 4) > tolerance) {
            newErrors.duration = "Half day leave must be exactly 4 hours";
          }
          if (leaveType === "Short Leave" && Math.abs(hours - 3) > tolerance) {
            newErrors.duration = "Short leave must be exactly 3 hours";
          }
        }
      }
    }

    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      return;
    }

    setLoadingButton(true);
    try {
      const requestData = new FormData();
      requestData.append("user_id", userId);
      requestData.append("leave_type_id", selectedLeave.leave_type_id);
      requestData.append("start_date", formData.startDate);
      requestData.append("end_date", endDate);
      requestData.append("unit", leaveType.toLowerCase().replace(" ", "_"));
      requestData.append("reason", formData.description);

      if (leaveType !== "Full Day") {
        requestData.append("start_time", formData.startTime);
        requestData.append("end_time", formData.endTime);
      }

      if (formData.document) {
        requestData.append("document", formData.document);
      }

      const response = await apiUrl.post("/apply-leave", requestData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      if (response.data.status) {
        fetchUserLeaveEntitlement();
        setRequestRefreshKey((k) => k + 1);
        setLeaveModal(false);
        setFormData({
          startDate: "",
          endDate: "",
          description: "",
          document: null,
          startTime: "",
          endTime: "",
        });
        setSelectedLeave(null);
        setLeaveType("Full Day");
        setErrors({});

        Swal.fire({
          title: response.data.message,
          icon: "success",
          timer: 3000,
          showConfirmButton: false,
          toast: true,
          position: "top-end",
        });
      }
    } catch (error) {
      if (error.response?.data?.errors) {
        const backendErrors = {};
        Object.keys(error.response.data.errors).forEach((key) => {
          backendErrors[key] = error.response.data.errors[key][0];
        });
        setErrors(backendErrors);

        const errorMessages = Object.values(error.response.data.errors)
          .map((error) => error[0])
          .join("<br>");
        Swal.fire({
          title: "Validation Error",
          html: errorMessages,
          icon: "error",
          timer: 5000,
          showConfirmButton: false,
          toast: true,
          position: "top-end",
        });
      } else if (error.response?.data?.message) {
        setErrors({ general: error.response.data.message });

        Swal.fire({
          title: "Error",
          text: error.response.data.message,
          icon: "error",
          timer: 5000,
          showConfirmButton: false,
          toast: true,
          position: "top-end",
        });
      } else {
        setErrors({
          general: "Failed to submit leave request. Please try again.",
        });

        Swal.fire({
          title: "Error",
          text: "Failed to submit leave request. Please try again.",
          icon: "error",
          timer: 5000,
          showConfirmButton: false,
          toast: true,
          position: "top-end",
        });
      }

      setLoadingButton(false);
    } finally {
      setLoadingButton(false);
    }
  };
  return (
    <>
      <main className="container py-4">
        <div className="lp-leave-page-header mb-4">
          <div>
            <h1 className="lp-leave-page-title">My Leave</h1>
            <p className="lp-leave-page-subtitle mb-0">
              Your active balances and submitted applications
            </p>
          </div>
          <button
            type="button"
            onClick={() => openApplyModal()}
            className="btn btn-primary d-inline-flex align-items-center gap-2"
            disabled={leaveData.length === 0}
          >
            <FaIcons.FaPlusCircle />
            Apply Leave
          </button>
        </div>

        <h2 className="lp-section-title mb-3">
          <FaIcons.FaWallet />
          Leave Balances
        </h2>

        {showSkeleton ? (
          <div className="lp-leave-balance-grid mb-4">
            {[1, 2, 3].map((s) => (
              <div key={s} className="lp-balance-card placeholder-glow p-3">
                <span className="placeholder col-6 mb-2 d-block" />
                <span className="placeholder col-4 mb-3 d-block" />
                <span className="placeholder col-12 d-block mb-2" style={{ height: 48 }} />
                <span className="placeholder col-5 d-block" />
              </div>
            ))}
          </div>
        ) : leaveData?.length === 0 ? (
          <div className="lp-section-card mb-4">
            <div className="lp-empty-state py-5">
              <div className="empty-icon">
                <FaIcons.FaCalendarTimes size={28} />
              </div>
              <h5 className="fw-semibold">No Active Leave Balance</h5>
              <p className="text-muted small mb-0">
                Contact HR to assign leave entitlements for your branch.
              </p>
            </div>
          </div>
        ) : (
          <div className="lp-leave-balance-grid mb-4">
            {leaveData?.map((leave) => {
              const used = parseFloat(leave.used_days) || 0;
              const total = parseFloat(leave.total_days) || 0;
              const remaining = leave.remaining_days ?? total - used;
              const percent = total > 0 ? Math.min(100, (used / total) * 100) : 0;
              const canApply = remaining > 0;

              return (
                <div key={leave.id} className="lp-balance-card">
                  <div className="lp-balance-accent" />
                  <div className="lp-balance-body d-flex flex-column gap-3 flex-grow-1">
                    <div className="d-flex justify-content-between align-items-start gap-2">
                      <div>
                        <div className="lp-balance-title">{leave?.leave_type?.name}</div>
                        <span className="lp-balance-code">{leave?.leave_type?.code}</span>
                      </div>
                      <div className="lp-balance-remaining-big">
                        <span className={`lp-balance-remaining-num ${remaining <= 0 ? "low" : ""}`}>
                          {remaining}
                        </span>
                        <span className="lp-balance-remaining-label">days left</span>
                      </div>
                    </div>

                    <div className="lp-balance-period">
                      <div className="lp-balance-period-label">
                        <FaIcons.FaCalendarAlt className="me-1" />
                        Leave valid for this period
                      </div>
                      <div className="lp-balance-period-range">
                        <div className="lp-balance-period-item">
                          <span className="lp-balance-period-key">From</span>
                          <span className="lp-balance-period-val">
                            {formatPeriodDate(leave?.period_start)}
                          </span>
                        </div>
                        <div className="lp-balance-period-arrow">
                          <FaIcons.FaLongArrowAltRight />
                        </div>
                        <div className="lp-balance-period-item">
                          <span className="lp-balance-period-key">To</span>
                          <span className="lp-balance-period-val">
                            {formatPeriodDate(leave?.period_end)}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div>
                      <div className="d-flex justify-content-between small mb-1">
                        <span className="text-muted">
                          Used <strong className="text-dark">{used}</strong> of{" "}
                          <strong className="text-dark">{total}</strong> days
                        </span>
                        <span className="text-muted">{Math.round(percent)}%</span>
                      </div>
                      <div className="lp-balance-progress">
                        <div
                          className="lp-balance-progress-bar"
                          style={{ width: `${percent}%` }}
                        />
                      </div>
                    </div>

                    <button
                      type="button"
                      className="lp-card-apply-btn"
                      disabled={!canApply}
                      onClick={() => openApplyModal(leave)}
                    >
                      <FaIcons.FaPaperPlane />
                      Apply Leave
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <LeaveRequestsPanel userId={userId} refreshKey={requestRefreshKey} />
      </main>
      {/* leave Modal */}
      {leaveModal && (
        <>
          <div
            className="modal-backdrop fade show"
            style={{
              display: "block",
              backgroundColor: "rgba(0,0,0,0.45)",
              zIndex: 1040,
            }}
            onClick={() => setLeaveModal(false)}
          />
          <div
            className="modal fade show d-block"
            tabIndex={-1}
            role="dialog"
            style={{ zIndex: 1050 }}
          >
            <div
              className="modal-dialog modal-dialog-centered modal-lg"
              style={{ maxWidth: 700 }}
            >
              <div
                className="modal-content border-0 rounded-4 shadow"
                style={{ overflow: "hidden" }}
              >
                <div
                  className="modal-header"
                  style={{
                    background: "#f7f8fa",
                    borderBottom: "1px solid #eef2f6",
                  }}
                >
                  <div>
                    <h5 className="modal-title" style={{ fontWeight: 700 }}>
                      Apply for Leave
                    </h5>
                  </div>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setLeaveModal(false)}
                  />
                </div>
                <div className="modal-body p-4">
                  <div className="row g-4">
                    {/* Select Leave */}
                    <div className="col-12">
                      <label className="form-label fw-semibold mb-2">
                        Select Leave <span className="text-danger">*</span>
                      </label>
                      <select
                        className="form-select"
                        value={selectedLeave?.id || ""}
                        onChange={handleLeaveChange}
                        style={{
                          borderColor: "#e9ecef",
                          transition: "border-color 0.3s ease",
                        }}
                        onFocus={(e) =>
                          (e.target.style.borderColor = "#4361ee")
                        }
                        onBlur={(e) => (e.target.style.borderColor = "#e9ecef")}
                      >
                        <option value="">Choose a leave type...</option>
                        {leaveData?.map((leave) => (
                          <option
                            key={leave?.id}
                            value={leave?.id}
                            disabled={leave?.remaining_days <= 0}
                            style={
                              leave?.remaining_days <= 0
                                ? {
                                    color: "#6c757d",
                                    fontStyle: "italic",
                                  }
                                : {}
                            }
                          >
                            {leave?.leave_type?.name} — {leave?.remaining_days}{" "}
                            day(s) available
                            {leave?.remaining_days <= 0
                              ? " (Not Available)"
                              : ""}
                          </option>
                        ))}
                      </select>
                      {errors?.leave_type && (
                        <div className="text-danger small mt-1">
                          {errors?.leave_type}
                        </div>
                      )}
                    </div>

                    {/* Leave Duration */}
                    {selectedLeave && (
                      <div className="col-12">
                        <label className="form-label fw-semibold mb-2">
                          Leave Duration <span className="text-danger">*</span>
                        </label>
                        <select
                          className="form-select"
                          value={leaveType}
                          onChange={(e) => setLeaveType(e.target.value)}
                          style={{
                            borderColor: "#e9ecef",
                            transition: "border-color 0.3s ease",
                          }}
                          onFocus={(e) =>
                            (e.target.style.borderColor = "#4361ee")
                          }
                          onBlur={(e) =>
                            (e.target.style.borderColor = "#e9ecef")
                          }
                        >
                          <option value="Full Day">Full Day</option>
                          {selectedLeave?.leave_type?.is_partial_allowed && (
                            <>
                              <option value="Half Day">Half Day</option>
                              <option value="Short Leave">Short Leave</option>
                            </>
                          )}
                        </select>
                      </div>
                    )}

                    {/* Date Selection */}
                    <div className="col-md-6">
                      <label className="form-label fw-semibold mb-2">
                        Start Date <span className="text-danger">*</span>
                      </label>
                      <input
                        type="date"
                        className="form-control "
                        value={formData?.startDate}
                        min={selectedLeave?.period_start?.split("T")[0]}
                        max={selectedLeave?.period_end?.split("T")[0]}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            startDate: e.target.value,
                          })
                        }
                        style={{
                          borderColor: "#e9ecef",
                          transition: "border-color 0.3s ease",
                        }}
                        onFocus={(e) =>
                          (e.target.style.borderColor = "#4361ee")
                        }
                        onBlur={(e) => (e.target.style.borderColor = "#e9ecef")}
                      />
                      {errors.startDate && (
                        <div className="text-danger small mt-1">
                          {errors.startDate}
                        </div>
                      )}
                    </div>

                    <div className="col-md-6">
                      <label className="form-label fw-semibold mb-2">
                        End Date
                      </label>
                      <input
                        type="date"
                        className="form-control "
                        value={formData?.endDate}
                        min={selectedLeave?.period_start?.split("T")[0]}
                        max={selectedLeave?.period_end?.split("T")[0]}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            endDate: e.target.value,
                          })
                        }
                        style={{
                          borderColor: "#e9ecef",
                          transition: "border-color 0.3s ease",
                        }}
                        onFocus={(e) =>
                          (e.target.style.borderColor = "#4361ee")
                        }
                        onBlur={(e) => (e.target.style.borderColor = "#e9ecef")}
                      />
                      {errors.endDate && (
                        <div className="text-danger small mt-1">
                          {errors.endDate}
                        </div>
                      )}
                    </div>
                    {/* Time selection for Half Day / Short Leave */}
                    {(leaveType === "Half Day" ||
                      leaveType === "Short Leave") && (
                      <>
                        <div className="col-md-6">
                          <label className="form-label fw-semibold mb-2">
                            Start Time <span className="text-danger">*</span>
                          </label>
                          <input
                            type="time"
                            className="form-control"
                            value={formData.startTime}
                            onChange={(e) => {
                              const selectedTime = e.target.value;
                              const [hours, minutes] = selectedTime
                                .split(":")
                                .map(Number);
                              let timeError = null;

                              if (
                                hours < 9 ||
                                hours > 17 ||
                                (hours === 17 && minutes > 0)
                              ) {
                                timeError =
                                  "Start time must be between 9:00 AM and 5:00 PM.";
                              }

                              setFormData({
                                ...formData,
                                startTime: selectedTime,
                              });
                              setErrors({ ...errors, startTime: timeError });
                            }}
                            min="09:00"
                            max="17:00"
                          />
                          {errors.startTime && (
                            <div className="text-danger small mt-1">
                              {errors.startTime}
                            </div>
                          )}
                        </div>

                        <div className="col-md-6">
                          <label className="form-label fw-semibold mb-2">
                            End Time <span className="text-danger">*</span>
                          </label>
                          <input
                            type="time"
                            className="form-control"
                            value={formData.endTime}
                            onChange={(e) => {
                              const selectedTime = e.target.value;
                              const [hours, minutes] = selectedTime
                                .split(":")
                                .map(Number);
                              let timeError = null;

                              if (
                                hours < 9 ||
                                hours > 17 ||
                                (hours === 17 && minutes > 0)
                              ) {
                                timeError =
                                  "End time must be between 9:00 AM and 5:00 PM.";
                              }

                              setFormData({
                                ...formData,
                                endTime: selectedTime,
                              });
                              setErrors({ ...errors, endTime: timeError });
                            }}
                            min="09:00"
                            max="17:00"
                          />
                          {errors.endTime && (
                            <div className="text-danger small mt-1">
                              {errors.endTime}
                            </div>
                          )}
                          {errors.duration && (
                            <div className="text-danger small mt-1">
                              {errors.duration}
                            </div>
                          )}
                        </div>
                      </>
                    )}

                    {/* Description */}
                    <div className="col-12">
                      <label className="form-label fw-semibold mb-2">
                        Reason for Leave <span className="text-danger">*</span>
                      </label>
                      <textarea
                        className="form-control "
                        placeholder="Please provide a detailed reason for your leave request..."
                        rows="3"
                        value={formData?.description}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            description: e.target.value,
                          })
                        }
                        style={{
                          borderColor: "#e9ecef",
                          transition: "border-color 0.3s ease",
                          resize: "vertical",
                        }}
                        onFocus={(e) =>
                          (e.target.style.borderColor = "#4361ee")
                        }
                        onBlur={(e) => (e.target.style.borderColor = "#e9ecef")}
                      ></textarea>
                      {errors.description && (
                        <div className="text-danger small mt-1">
                          {errors.description}
                        </div>
                      )}
                    </div>

                    {/* Document Upload */}
                    {selectedLeave?.leave_policy?.requires_document && (
                      <div className="col-12">
                        <label className="form-label fw-semibold mb-2">
                          Supporting Document{" "}
                          <span className="text-danger">*</span>
                        </label>
                        <div
                          className="border-2 border-dashed rounded-3 p-4 text-center bg-light"
                          style={{
                            borderColor: "#e9ecef",
                            transition: "border-color 0.3s ease",
                            cursor: "pointer",
                          }}
                          onClick={() =>
                            document.getElementById("document-upload").click()
                          }
                        >
                          <div className="mb-2">
                            <FaIcons.FaFileUpload className="fs-1 text-muted" />
                          </div>
                          <p className="text-muted mb-0">
                            <strong>Click to upload</strong> or drag and drop
                          </p>
                          <p className="text-muted small mb-0">
                            PDF, JPG, PNG, DOC (Max 5MB)
                          </p>
                          <input
                            id="document-upload"
                            type="file"
                            className="d-none"
                            accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                document: e.target.files[0],
                              })
                            }
                          />
                        </div>
                        {formData?.document && (
                          <div className="mt-2 alert alert-success p-2">
                            <small>
                              <FaIcons.FaCheckCircle className="me-1" />
                              {formData.document.name} selected
                            </small>
                          </div>
                        )}
                      </div>
                    )}
                    {errors.document && (
                      <div className="text-danger small mt-1">
                        {errors.document}
                      </div>
                    )}
                  </div>
                </div>

                {/* Footer actions */}
                <div
                  className="modal-footer"
                  style={{
                    background: "#f7f8fa",
                    borderTop: "1px solid #eef2f6",
                  }}
                >
                  <button
                    className="btn btn-outline-secondary"
                    onClick={() => setLeaveModal(false)}
                  >
                    Close
                  </button>
                  <button
                    className="btn btn-success"
                    onClick={handleSubmitLeave}
                    disabled={loadingButton}
                  >
                    {loadingButton ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" />
                        Applying
                      </>
                    ) : (
                      <>Apply</>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
}

export default UserLeave;
